import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import React, { FC, useCallback, useEffect, useState } from 'react';
import postDrugSearch, {
  PostDrugRequest,
  PostDrugResponse
} from 'src/api/requests/postDrugSearch';
import {
  Category,
  DrugItem,
  FirstCategoryLevel,
  SecondCategoryLevel,
  SortBy,
  SortDirection,
  SubCategory
} from 'src/api/types';
import { useRouter } from 'next/router';
import useLocationStore from 'src/hooks/useLocationStore';
import { extractPageNumber } from 'src/utils/links';
import Head from 'next/head';
import {
  getCategoryPageMetaData,
  getSchemaData,
  isNoIndexing
} from 'src/utils/seo';
import { useIntl } from 'react-intl';
import { getSearchRequestObject } from 'src/api/hooks/usePostSearchFilters';
import {
  CustomErrorProps,
  useHandleCustomErrorRedirect,
  triggerCustomStatusCodeRedirect
} from 'src/ssr/errorStatusCodeRedirect';
import { fetchCache } from 'src/api/caching/cacheUtils';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import getMultyLevelCategories from 'src/api/requests/getMultyLevelCategories';
import getSpecificCategory from 'src/api/requests/getSpecificCategory';
import ThirdLevelCategoryPage, {
  getServerSideProps as thirdLevelCategoryGetServerSideProps
} from 'src/page-components/category/ThirdLevelCategoryPage';
import CategoryContainerSecondLevel from 'src/page-components/category/CategoryContainerSecondLevel';
import logger from 'src/utils/logger';
import useStoreDeep from 'src/hooks/useStore';
import useUserLocation from 'src/hooks/useUserLocation';
import FirstLevelCategoryPage, {
  getServerSideProps as firstLevelCategoryGetServerSideProps,
  FirstLevelCategoryProps
} from './index';
import {
  GeneralServerProps,
  generalSSRProps,
  getCacheConfig,
  isQueryParamsValid,
  setSSRCacheControl
} from '../../ssr/generalSsrUtil';
import useGTAG from '../../hooks/useGTAG';
import { PROD_DOMAIN } from '../../api/constants';
import { Locales } from '../../utils/localization';
import usePagination from '../../hooks/usePagination';

export interface Props extends GeneralServerProps, CustomErrorProps {
  categoriesList: Category[];
  multyLevelCategoriesList: FirstCategoryLevel[];
  firstLevelCategory: FirstCategoryLevel;
  secondLevelCategory: SecondCategoryLevel;
  categoriesInfo: SecondCategoryLevel;
  ssrSelectedCategory: Category;
  ssrSelectedSubCategory: SubCategory;
  ssrDrugSearchResponse: PostDrugResponse;
  popularDrugs: PostDrugResponse;
  cheapDrugs: PostDrugResponse;
  isFiltering: boolean;
  isSorting: boolean;
  pageNumber: number;
  ssrSearchObject: PostDrugRequest;
}

const CatalogPage: FC<Props | FirstLevelCategoryProps> = (props) => {
  const {
    firstLevelCategory,
    multyLevelCategoriesList,
    categoriesInfo,
    ssrDrugSearchResponse,
    popularDrugs,
    cheapDrugs,
    isFiltering,
    isSorting,
    pageNumber,
    hasError,
    statusCode,
    ssrSearchObject
  } = props;
  const { query, locale } = useRouter();
  const storeInitilized = useStoreDeep(
    useLocationStore,
    (state) => state.storeInitilized
  );
  const userLocation = useUserLocation();
  const { redirectToStatusCodePage } = useHandleCustomErrorRedirect({
    hasError,
    statusCode
  });

  const intl = useIntl();
  const { filters, sortBy, sortDirection } = query;

  const [drugList, setDrugList] = useState<DrugItem[]>(
    ssrDrugSearchResponse?.drugs || []
  );
  const [searchCount, setSearchCount] = useState<number>(0);
  const [totalDrugsCount, setTotalDrugsCount] = useState<number>(
    ssrDrugSearchResponse?.totalCount || 1
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([pageNumber]);
  const queryParams = Array.isArray(query.nextParams) ? query.nextParams : [];
  const paginationData = usePagination(
    totalDrugsCount,
    queryParams,
    PROD_DOMAIN,
    locale,
    Locales,
    pageNumber,
    16
  );

  useEffect(() => {
    setSelectedPages([pageNumber]);
  }, [pageNumber]);

  const getSearchObject = useCallback(
    (loadMorePageNumber?: number) => {
      const searchObject = getSearchRequestObject(
        loadMorePageNumber || pageNumber,
        null,
        filters,
        sortBy,
        sortDirection
      );
      return {
        ...searchObject,
        filters: [
          ...searchObject.filters,
          {
            id: 'categoryId',
            valueIds: [categoriesInfo?.translitName || '']
          }
        ]
      };
    },
    [pageNumber, categoriesInfo?.translitName, filters, sortBy, sortDirection]
  );

  const debouncedDrugsUpdate = useCallback(
    debounce(() => {
      const newSearchObject = getSearchObject();
      if (isEqual(newSearchObject, ssrSearchObject)) {
        // new request object is the same as was for initial page render
        // set SSR values to state instead of API calls
        setDrugList(ssrDrugSearchResponse?.drugs || []);
        setTotalDrugsCount(ssrDrugSearchResponse?.totalCount || 1);
        setIsLoading(false);
      } else {
        setIsLoading(true);
        const drugSearchRequest = postDrugSearch(getSearchObject());
        drugSearchRequest
          .then((drugSearchResult) => {
            setDrugList(drugSearchResult?.drugs || []);
            setTotalDrugsCount(drugSearchResult?.totalCount || 1);
          })
          .catch(() => {
            // Clean up drug list because error happend
            setDrugList([]);
            setTotalDrugsCount(1);
          })
          .finally(() => {
            setIsLoading(false);
            setSearchCount((count) => count + 1);
          });
      }
    }, 250),
    [
      pageNumber,
      categoriesInfo?.translitName,
      userLocation?.name,
      locale,
      filters,
      sortBy,
      sortDirection
    ]
  );

  useEffect(() => {
    if (storeInitilized) {
      debouncedDrugsUpdate();
    }
  }, [debouncedDrugsUpdate, storeInitilized]);

  const { trackGtagEvent } = useGTAG();

  const handleLoadMoreSubmit = (loadMorePageNumber: number) => {
    setIsLoading(true);
    const drugSearchRequest = postDrugSearch(
      getSearchObject(loadMorePageNumber)
    );
    drugSearchRequest
      .then((drugSearchResult) => {
        // Add newly loaded drugs to the existing list
        const extendedDrugs = [...drugList, ...(drugSearchResult?.drugs || [])];
        setDrugList(extendedDrugs);
        setSelectedPages([...selectedPages, loadMorePageNumber]);
      })
      .catch((error) => {
        logger.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        setSearchCount((count) => count + 1);
        setTimeout(() => {
          trackGtagEvent('subcategory_load_more');
        }, 250);
      });
  };

  if ('isFirstLevelCategoryPage' in props) {
    return <FirstLevelCategoryPage {...props} />;
  }

  if ('isThirdLevelCategoryPage' in props) {
    return <ThirdLevelCategoryPage {...props} />;
  }

  if (hasError) {
    return redirectToStatusCodePage();
  }

  const seoData = getCategoryPageMetaData(
    categoriesInfo?.name,
    pageNumber,
    locale
  );

  const getIndex = () => {
    if (pageNumber > 1 || isSorting) return 'noindex, follow';
    if (isNoIndexing) return 'noindex';
    if (isFiltering) return 'noindex, nofollow';
    return 'index, follow';
  };
  const hrefSeo = `${paginationData.mainPathPart}/${query.category}/${query.nextParams[0]}`;

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {paginationData.prevPage && (
          <link
            rel="prev"
            href={`${hrefSeo}?page-${paginationData.prevPage}`}
          />
        )}
        {paginationData.nextPage && (
          <link
            rel="next"
            href={`${hrefSeo}?page-${paginationData.nextPage}`}
          />
        )}
        {/* Prevent indexing if filtering or sorting provided */}

        <meta name="robots" content={getIndex()} key="robots" />

        {!isFiltering ||
          (!isSorting && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  getSchemaData(
                    categoriesInfo?.name,
                    popularDrugs,
                    cheapDrugs,
                    intl
                  )
                )
              }}
            />
          ))}
      </Head>
      <CategoryContainerSecondLevel
        drugList={drugList}
        multyLevelCategoriesList={multyLevelCategoriesList}
        selectedFirstLevelCategory={firstLevelCategory}
        selectedSecondLevelCategory={categoriesInfo}
        totalDrugsCount={totalDrugsCount}
        selectedPages={selectedPages}
        isLoading={isLoading}
        handleLoadMoreSubmit={handleLoadMoreSubmit}
        isSEOTextHidden={isFiltering || isSorting || pageNumber > 1}
        finalEntityName={categoriesInfo?.name}
        popularDrugs={popularDrugs}
        cheapDrugs={popularDrugs}
        searchObject={getSearchObject()}
        searchCount={searchCount}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchCache<GetServerSidePropsResult<{ [key: string]: any }>>(
    getCacheConfig(context),
    async () => {
      const {
        res,
        req: { cookies },
        query
      } = context;
      const generalProps = await generalSSRProps(context);
      if (!isQueryParamsValid(context.query)) {
        // Redirect to 410 because the URL contains invalid query params
        return triggerCustomStatusCodeRedirect(context.res, 410);
      }

      const { category, nextParams, filters, sortBy, sortDirection } = query;
      const [secondParamsSlug, thirdParamsSlug] = nextParams as string[];
      const pageNumber = extractPageNumber(thirdParamsSlug);

      const multyLevelCategoriesList = await getMultyLevelCategories({
        cookies,
        ip: generalProps.ip,
        locale: context?.locale,
        useCache: true
      });

      // Second category info
      const categoriesInfo = await getSpecificCategory(
        {
          level: '1',
          translitName: secondParamsSlug as string
        },
        { cookies, ip: generalProps.ip, locale: context?.locale }
      );

      if (secondParamsSlug.startsWith('page')) {
        // this case happens if user tries to access pagination for first level category
        // return first level category props and render first level category container
        return firstLevelCategoryGetServerSideProps(context);
      }

      if (thirdParamsSlug && !thirdParamsSlug?.startsWith('page')) {
        // this case happens if user tries to access third level category
        // return third level category props and render third level category container
        return thirdLevelCategoryGetServerSideProps(context);
      }

      const firstLevelCategory = multyLevelCategoriesList?.find(
        (subC) => subC?.translitName === (category as string)
      );

      const searchObject = getSearchRequestObject(
        pageNumber,
        null,
        filters,
        sortBy,
        sortDirection
      );

      // a search object used for initial load
      // it's utilized later on the client side to reduce calls to API
      const ssrSearchObject = {
        ...searchObject,
        filters: [
          {
            id: 'categoryId',
            valueIds: [categoriesInfo?.translitName || '']
          },
          ...searchObject.filters
        ]
      };

      const drugSearchResponse = await postDrugSearch(ssrSearchObject, {
        cookies,
        ip: generalProps.ip,
        locale: context?.locale,
        useCache: true
      });

      if (
        thirdParamsSlug &&
        parseInt(thirdParamsSlug.split('page-')[1], 10) &&
        !drugSearchResponse?.drugs?.length
      ) {
        // Redirect to 410 if pagination page not found
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      const isFiltering = !!filters;
      const isSorting = !!sortBy || !!sortDirection;

      const getRequestBody = (sortByParam: SortBy) => {
        const body = getSearchRequestObject(
          1,
          null,
          filters,
          sortByParam,
          SortDirection.Asc,
          3
        );
        return {
          ...body,
          filters: [
            {
              id: 'categoryId',
              valueIds: [(categoriesInfo?.translitName as string) || '']
            },
            ...body.filters
          ]
        };
      };
      const [popularDrugs, cheapDrugs] = await Promise.all([
        postDrugSearch(getRequestBody(SortBy.popularity)),
        postDrugSearch(getRequestBody(SortBy.price))
      ]);

      if (!firstLevelCategory?.translitName || !categoriesInfo) {
        // Redirect to 410 if letter not found
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      return {
        props: {
          ...generalProps,
          firstLevelCategory,
          multyLevelCategoriesList,
          categoriesInfo,
          ssrDrugSearchResponse: drugSearchResponse,
          popularDrugs,
          cheapDrugs,
          isFiltering,
          isSorting,
          pageNumber,
          ssrSearchObject
        }
      };
    }
  );
};

export default CatalogPage;
